/**
 * App.jsx — Composant racine de l'application Burdigala Cornhole Club
 *
 * ============================================================
 * 📚 LEÇON : CODE SPLITTING avec React.lazy + Suspense
 * ============================================================
 *
 * AVANT (sans code splitting) :
 *   → Vite génère 1 seul fichier bundle.js contenant TOUT le code
 *   → Ex: 800 Ko chargé d'un coup même si l'utilisateur visite juste "/"
 *
 * APRÈS (avec code splitting) :
 *   → Vite génère 1 chunk par page (ex: Home-abc123.js = 30Ko)
 *   → Seul le code de la page visitée est téléchargé
 *   → Résultat : bundle initial ~5x plus léger = FCP (First Contentful Paint) amélioré
 *
 * COMMENT ÇA MARCHE ?
 *   1. React.lazy(() => import('./pages/Home'))
 *      → Transforme l'import statique en import dynamique
 *      → Vite crée automatiquement un chunk séparé pour chaque page
 *
 *   2. <Suspense fallback={<PageLoader />}>
 *      → Pendant que le chunk se télécharge, affiche le composant fallback
 *      → Sans Suspense, React lève une erreur si le composant n'est pas prêt
 *
 * ============================================================
 */

import React, { useState, useEffect, lazy, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom'

// ─────────────────────────────────────────────────────────────────────────────
// IMPORTS STATIQUES — Ces composants sont chargés immédiatement
// Pourquoi ? Parce qu'ils sont présents sur TOUTES les pages.
// Les charger en lazy n'apporterait aucun gain.
// ─────────────────────────────────────────────────────────────────────────────
import ScrollToAnchor from './components/ScrollToAnchor'
import ThemeToggle from './components/atoms/ThemeToggle'
import PageLoader from './components/atoms/PageLoader'
import { useParallax } from './hooks'
import { MAINTENANCE_MODE } from './config/maintenance'
import './App.css'
import './styles/animations.css'

// ─────────────────────────────────────────────────────────────────────────────
// IMPORTS DYNAMIQUES (LAZY) — Chargés uniquement quand la route est visitée
//
// 📚 LEÇON : Pourquoi les commentaires /* webpackChunkName */ ?
//   → Vite/Rollup utilise ces commentaires pour nommer les chunks générés
//   → Sans commentaire : "chunk-a1b2c3.js" (illisible dans les DevTools réseau)
//   → Avec commentaire  : "Home-a1b2c3.js" (clair pour déboguer)
// ─────────────────────────────────────────────────────────────────────────────

// — Pages principales (chargées fréquemment)
const Home             = lazy(() => import(/* webpackChunkName: "Home" */             './pages/Home'))
const NotreAssociation = lazy(() => import(/* webpackChunkName: "NotreAssociation" */ './pages/NotreAssociation'))
const Contact          = lazy(() => import(/* webpackChunkName: "Contact" */          './pages/Contact'))
const Partenaires      = lazy(() => import(/* webpackChunkName: "Partenaires" */      './pages/Partenaires'))
const Adhesion         = lazy(() => import(/* webpackChunkName: "Adhesion" */         './pages/Adhesion'))

// — Section Blog / Actualités
const Actualites        = lazy(() => import(/* webpackChunkName: "Actualites" */        './pages/Actualites'))
const InterviewMathieu  = lazy(() => import(/* webpackChunkName: "InterviewMathieu" */  './pages/InterviewMathieu'))
const InterviewOusmane  = lazy(() => import(/* webpackChunkName: "InterviewOusmane" */  './pages/InterviewOusmane'))
const PetanqueCornhole  = lazy(() => import(/* webpackChunkName: "PetanqueCornhole" */  './pages/PetanqueCornhole'))
const TechniqueCornhole = lazy(() => import(/* webpackChunkName: "TechniqueCornhole" */ './pages/TechniqueCornhole'))
const SacsCornhole      = lazy(() => import(/* webpackChunkName: "SacsCornhole" */      './pages/SacsCornhole'))
const ForumAssociations = lazy(() => import(/* webpackChunkName: "ForumAssociations" */ './pages/ForumAssociations'))
const CourseOrientation = lazy(() => import(/* webpackChunkName: "CourseOrientation" */ './pages/CourseOrientation'))
const NouveauSite       = lazy(() => import(/* webpackChunkName: "NouveauSite" */       './pages/NouveauSite'))
const TournoiLLBRecap        = lazy(() => import(/* webpackChunkName: "TournoiLLBRecap" */        './pages/TournoiLLBRecap'))
const TournoiParthenayRecap  = lazy(() => import(/* webpackChunkName: "TournoiParthenayRecap" */  './pages/TournoiParthenayRecap'))

// — Section Cornhole
const Regles = lazy(() => import(/* webpackChunkName: "Regles" */ './pages/Regles'))

// — Section Événements & Tournois
const AnimeTesVacances    = lazy(() => import(/* webpackChunkName: "AnimeTesVacances" */ './pages/AnimeTesVacances'))
const CaveDeZae           = lazy(() => import(/* webpackChunkName: "CaveDeZae" */        './pages/CaveDeZae'))
const TournoiLLB2026      = lazy(() => import(/* webpackChunkName: "TournoiLLB2026" */   './pages/TournoiLLB2026'))
const TournoiMarolles2026    = lazy(() => import(/* webpackChunkName: "TournoiMarolles" */     './pages/TournoiMarolles2026'))
const TournoiBagsOnFire2026  = lazy(() => import(/* webpackChunkName: "TournoiBagsOnFire" */   './pages/TournoiBagsOnFire2026'))
const TournoiLandes2026     = lazy(() => import(/* webpackChunkName: "TournoiLandes2026" */    './pages/TournoiLandes2026'))
const TournoiParthenay2026  = lazy(() => import(/* webpackChunkName: "TournoiParthenay" */   './pages/TournoiParthenay2026'))

// — Galerie & Classements
const Galerie     = lazy(() => import(/* webpackChunkName: "Galerie" */     './pages/Galerie'))
const Classements = lazy(() => import(/* webpackChunkName: "Classements" */ './pages/Classements'))

// — Pages système
const Maintenance = lazy(() => import(/* webpackChunkName: "Maintenance" */ './pages/Maintenance'))
const NotFound    = lazy(() => import(/* webpackChunkName: "NotFound" */    './pages/NotFound'))

// ─────────────────────────────────────────────────────────────────────────────
// COMPOSANT : AnimatedRoutes
//
// Séparé de App pour pouvoir utiliser useLocation()
// (useLocation() nécessite d'être dans un enfant de <Router>)
// ─────────────────────────────────────────────────────────────────────────────
function AnimatedRoutes() {
  const location = useLocation()

  // Mode maintenance : on court-circuite toutes les routes
  if (MAINTENANCE_MODE) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Maintenance />
      </Suspense>
    )
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 📚 LEÇON : IntersectionObserver pour les animations au scroll
  //
  // useEffect se déclenche à chaque changement de route (location.pathname).
  // Sans ce reset, les éléments déjà animés sur la page précédente
  // garderaient la classe "visible" même après navigation.
  //
  // threshold: 0.1  = déclenche quand 10% de l'élément est visible
  // rootMargin: -100px = déclenche 100px avant le bord bas du viewport
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px',
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible')
        }
      })
    }, observerOptions)

    // Délai pour laisser le DOM se rendre après le changement de route
    const timeoutId = setTimeout(() => {
      document.querySelectorAll('.animate-on-scroll').forEach((el) => {
        el.classList.remove('visible') // Reset avant ré-observation
        observer.observe(el)
      })
    }, 100)

    // Cleanup : déconnecter l'observer quand la route change
    return () => {
      clearTimeout(timeoutId)
      observer.disconnect()
    }
  }, [location.pathname])

  // ─────────────────────────────────────────────────────────────────────────
  // 📚 LEÇON : UN SEUL <Suspense> pour toutes les routes
  //
  // On wrap TOUTES les routes dans un seul Suspense plutôt qu'un par route.
  // → Évite la répétition du fallback
  // → Centralise la gestion du chargement
  //
  // key={location.pathname} sur <Routes> force React à recréer l'arbre
  // de composants à chaque changement de route → reset des states locaux
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes location={location} key={location.pathname}>
        {/* — Pages principales */}
        <Route path="/"                  element={<Home />} />
        <Route path="/notre-association" element={<NotreAssociation />} />
        <Route path="/contact"           element={<Contact />} />
        <Route path="/partenaires"       element={<Partenaires />} />
        <Route path="/adhesion"          element={<Adhesion />} />

        {/* — Blog & Actualités */}
        <Route path="/actualites"                    element={<Actualites />} />
        <Route path="/actualites/interview-mathieu"  element={<InterviewMathieu />} />
        <Route path="/actualites/interview-ousmane"  element={<InterviewOusmane />} />
        <Route path="/actualites/petanque-cornhole"  element={<PetanqueCornhole />} />
        <Route path="/actualites/technique-cornhole" element={<TechniqueCornhole />} />
        {/* ✅ NOUVEL ARTICLE — Guide d'achat sacs cornhole 2026 */}
        <Route path="/actualites/sacs-cornhole"      element={<SacsCornhole />} />
        {/* ✅ NOUVEL ARTICLE — Forum des associations 2026 */}
        <Route path="/actualites/forum-associations" element={<ForumAssociations />} />
        {/* ✅ NOUVEL ARTICLE — Course d'orientation solidaire Octobre Rose 2026 */}
        <Route path="/actualites/course-orientation-octobre-rose" element={<CourseOrientation />} />
        <Route path="/actualites/nouveau-site"       element={<NouveauSite />} />
        <Route path="/actualites/tournoi-llb-recap"      element={<TournoiLLBRecap />} />
        {/* ✅ NOUVEL ARTICLE — Retour sur la Final Parthenay 2026 */}
        <Route path="/actualites/tournoi-parthenay-recap" element={<TournoiParthenayRecap />} />

        {/* — Cornhole */}
        <Route path="/cornhole/regles" element={<Regles />} />

        {/* — Événements & Tournois */}
        <Route path="/evenements/anime-tes-vacances" element={<AnimeTesVacances />} />
        <Route path="/evenements/cave-de-zae"        element={<CaveDeZae />} />
        <Route path="/tournois/llb-2026"             element={<TournoiLLB2026 />} />
        <Route path="/tournois/marolles-2026"        element={<TournoiMarolles2026 />} />
        <Route path="/tournois/bags-on-fire-2026"    element={<TournoiBagsOnFire2026 />} />
        <Route path="/tournois/landes-2026"          element={<TournoiLandes2026 />} />
        <Route path="/tournois/parthenay-2026"    element={<TournoiParthenay2026 />} />

        {/* — Galerie & Classements */}
        <Route path="/galerie"     element={<Galerie />} />
        <Route path="/classements" element={<Classements />} />

        {/* — Pages système */}
        <Route path="/maintenance" element={<Maintenance />} />
        <Route path="*"            element={<NotFound />} />
      </Routes>
    </Suspense>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPOSANT RACINE : App
//
// Responsabilités :
//   1. Fournir le contexte Router à toute l'app
//   2. Gérer l'effet parallax sur le fond via useParallax (optimisé rAF)
//   3. Afficher le bouton ThemeToggle global
// ─────────────────────────────────────────────────────────────────────────────
function App() {
  // Déclenche l'animation d'entrée CSS via la classe "loaded"
  const [isLoaded, setIsLoaded] = useState(false)

  // ── Parallax optimisé ──────────────────────────────────────────────────
  // 📚 LEÇON : Avant, on avait useState + mousemove directement ici.
  // Le hook useParallax encapsule la même logique MAIS avec rAF :
  //   → Maximum 1 setState par frame (60fps), pas 200x/seconde
  //   → passive: true sur l'écouteur → scroll plus fluide sur mobile
  //   → cancelAnimationFrame au cleanup → pas de fuite mémoire
  //   → Mise en pause auto quand l'onglet est en arrière-plan
  //
  // intensity=20 : amplitude max en px (même valeur qu'avant)
  // factor=0.5   : multiplicateur final (même valeur qu'avant)
  // → Visuellement identique, ~3-4x moins de renders
  const mousePosition = useParallax(20, 0.5)

  useEffect(() => {
    setIsLoaded(true)
  }, [])

  return (
    <Router>
      {/* Remonte automatiquement en haut de page lors d'un changement de route */}
      <ScrollToAnchor />

      <div className={`app ${isLoaded ? 'loaded' : ''}`}>
        {/* Fond animé avec effet parallax — throttlé via requestAnimationFrame */}
        <div
          className="parallax-bg"
          style={{
            transform: `translate(${mousePosition.x}px, ${mousePosition.y}px)`,
          }}
        />

        {/* Bouton thème clair/sombre — présent sur toutes les pages */}
        <ThemeToggle />

        {/* Toutes les routes de l'application avec code splitting actif */}
        <AnimatedRoutes />
      </div>
    </Router>
  )
}

export default App
